import React from 'react';

export const Card = ({ children, style, ...props }) => (
  <div style={{
    background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)',
    boxShadow: 'var(--shadow)', padding: 24, ...style
  }} {...props}>{children}</div>
);

export const Button = ({ children, variant = 'primary', style, ...props }) => {
  const variants = {
    primary: { background: 'var(--primary)', color: 'white' },
    danger: { background: '#ef4444', color: 'white' },
    success: { background: '#22c55e', color: 'white' },
    secondary: { background: 'var(--bg-input)', color: 'var(--text)', border: '1px solid var(--border)' },
    ghost: { background: 'transparent', color: 'var(--text)' },
  };
  return (
    <button style={{
      padding: '10px 20px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
      fontSize: 14, fontWeight: 500, transition: 'all 0.2s', display: 'inline-flex',
      alignItems: 'center', gap: 8, ...variants[variant], ...style,
      opacity: props.disabled ? 0.5 : 1
    }} {...props}>{children}</button>
  );
};

export const Input = ({ label, style, ...props }) => (
  <div style={{ marginBottom: 16, ...style }}>
    {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>{label}</label>}
    <input style={{
      width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
      background: 'var(--bg-input)', color: 'var(--text)', fontSize: 14, outline: 'none',
      transition: 'border-color 0.2s'
    }} {...props} />
  </div>
);

export const Select = ({ label, options, style, ...props }) => (
  <div style={{ marginBottom: 16, ...style }}>
    {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>{label}</label>}
    <select style={{
      width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
      background: 'var(--bg-input)', color: 'var(--text)', fontSize: 14, outline: 'none'
    }} {...props}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

/**
 * A select you can type into.
 *
 * A plain <select> is fine for five options and unusable for two hundred, which
 * is what an application inventory looks like. The typed text filters the list;
 * the value handed back is still the option's id, so callers do not have to map
 * a label back to a record.
 *
 * The input shows the chosen option's label when one is chosen and closed, and
 * whatever is being typed while open — so it reads as a field with a value
 * rather than a search box that forgets.
 */
export const Combobox = ({ label, options = [], value, onChange, placeholder, emptyText, style, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const boxRef = React.useRef(null);

  const selected = options.find(o => o.value === value) || null;

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const needle = query.trim().toLowerCase();
  const matches = needle
    ? options.filter(o => o.label.toLowerCase().includes(needle))
    : options;

  React.useEffect(() => { setActive(0); }, [query]);

  const choose = (opt) => {
    onChange(opt ? opt.value : '');
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      if (open && matches[active]) { e.preventDefault(); choose(matches[active]); }
    } else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  const field = {
    width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text)', fontSize: 14, outline: 'none',
  };

  return (
    <div style={{ marginBottom: 16, ...style }} ref={boxRef}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...field, paddingRight: 62, cursor: disabled ? 'not-allowed' : 'text', opacity: disabled ? 0.5 : 1 }}
          disabled={disabled}
          value={open ? query : (selected ? selected.label : '')}
          placeholder={selected ? selected.label : (placeholder || 'Type to search...')}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      display: 'flex', gap: 4, alignItems: 'center' }}>
          {selected && !disabled && (
            <button type="button" onClick={() => choose(null)} title="Clear"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1 }}>
              ×
            </button>
          )}
          <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>▼</span>
        </div>

        {open && !disabled && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 4,
            maxHeight: 240, overflowY: 'auto', background: 'var(--bg-card, #1a1f2e)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            {matches.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
                {emptyText || 'Nothing matches'}
              </div>
            ) : matches.map((o, i) => (
              <div key={o.value}
                onMouseDown={e => { e.preventDefault(); choose(o); }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: '9px 14px', fontSize: 14, cursor: 'pointer',
                  background: i === active ? 'var(--bg-input)' : 'transparent',
                  color: o.value === value ? 'var(--primary, #3b82f6)' : 'var(--text)',
                  fontWeight: o.value === value ? 600 : 400,
                }}>
                {o.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const TextArea = ({ label, style, ...props }) => (
  <div style={{ marginBottom: 16, ...style }}>
    {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>{label}</label>}
    <textarea style={{
      width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
      background: 'var(--bg-input)', color: 'var(--text)', fontSize: 14, outline: 'none',
      minHeight: 100, resize: 'vertical', fontFamily: 'inherit'
    }} {...props} />
  </div>
);

export const Badge = ({ severity, children }) => {
  const colors = {
    Critical: { bg: '#fef2f2', color: '#991b1b', dbg: '#7f1d1d', dc: '#fca5a5' },
    High: { bg: '#fef2f2', color: '#991b1b', dbg: '#991b1b', dc: '#fca5a5' },
    Medium: { bg: '#fff7ed', color: '#9a3412', dbg: '#92400e', dc: '#fed7aa' },
    Low: { bg: '#fefce8', color: '#854d0e', dbg: '#713f12', dc: '#fde68a' },
    Info: { bg: '#eff6ff', color: '#1d4ed8', dbg: '#1e3a5f', dc: '#93c5fd' },
    New: { bg: '#eff6ff', color: '#1d4ed8', dbg: '#1e3a5f', dc: '#93c5fd' },
    Requested: { bg: '#eef2ff', color: '#4338ca', dbg: '#3730a3', dc: '#c7d2fe' },
    Scoping: { bg: '#f5f3ff', color: '#6d28d9', dbg: '#4c1d95', dc: '#c4b5fd' },
    'Contract Sent': { bg: '#ecfeff', color: '#0e7490', dbg: '#155e75', dc: '#a5f3fc' },
    'In Progress': { bg: '#dbeafe', color: '#1d4ed8', dbg: '#065f46', dc: '#6ee7b7' },
    'In Review': { bg: '#fff7ed', color: '#c2410c', dbg: '#92400e', dc: '#fed7aa' },
    Complete: { bg: '#dcfce7', color: '#15803d', dbg: '#14532d', dc: '#86efac' },
    // Teal for the retest phase and a deeper green for its end, so a
    // revalidation reads as its own leg of the work rather than as a
    // second Complete.
    'In Retest': { bg: '#f0fdfa', color: '#0f766e', dbg: '#134e4a', dc: '#5eead4' },
    'Retest Complete': { bg: '#ecfdf5', color: '#047857', dbg: '#064e3b', dc: '#6ee7b7' },
    Cancelled: { bg: '#f5f5f4', color: '#57534e', dbg: '#44403c', dc: '#d6d3d1' },
    Open: { bg: '#fef2f2', color: '#991b1b', dbg: '#991b1b', dc: '#fca5a5' },
    Closed: { bg: '#dcfce7', color: '#15803d', dbg: '#14532d', dc: '#86efac' },
    Accepted: { bg: '#f5f5f4', color: '#57534e', dbg: '#44403c', dc: '#d6d3d1' },
    Retest: { bg: '#f5f3ff', color: '#6d28d9', dbg: '#4c1d95', dc: '#c4b5fd' },
    Reopened: { bg: '#fff1f2', color: '#be123c', dbg: '#9f1239', dc: '#fda4af' },
    Vulnerable: { bg: '#fef2f2', color: '#991b1b', dbg: '#991b1b', dc: '#fca5a5' },
    'Not Vulnerable': { bg: '#dcfce7', color: '#15803d', dbg: '#14532d', dc: '#86efac' },
    'Flag for Follow Up': { bg: '#fff7ed', color: '#9a3412', dbg: '#92400e', dc: '#fed7aa' },
    Draft: { bg: '#f1f5f9', color: '#475569', dbg: '#334155', dc: '#cbd5e1' },
    'Waiting on Client': { bg: '#fefce8', color: '#854d0e', dbg: '#854d0e', dc: '#fde68a' },
    Active: { bg: '#eff6ff', color: '#1d4ed8', dbg: '#1e3a5f', dc: '#93c5fd' },
    'On Hold': { bg: '#fefce8', color: '#854d0e', dbg: '#854d0e', dc: '#fde68a' }
  };
  const isLight = document.documentElement.getAttribute('data-theme') === 'light';
  const c = colors[severity || children] || { bg: '#f1f5f9', color: '#475569', dbg: '#334155', dc: '#e2e8f0' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11,
      fontWeight: 600, background: isLight ? c.bg : c.dbg, color: isLight ? c.color : c.dc,
      letterSpacing: '0.01em'
    }}>{children || severity}</span>
  );
};

export const Modal = ({ isOpen, onClose, title, children, width = 600, noOverlayClose = false }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
    }} onClick={noOverlayClose ? undefined : onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', width: '100%',
        maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)'
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid var(--border)'
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 24, cursor: 'pointer',
            color: 'var(--text-secondary)', lineHeight: 1
          }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

// Expansion is uncontrolled by default. Pass expandedIds + onToggleExpand to
// drive it from the parent instead — lets a cell (not just the chevron) open a row.
// Severity and status are ranked, not alphabetical: sorting findings puts
// Critical next to Info if you compare the strings, which is the opposite of
// what anyone reading a findings table wants.
const RANKS = {
  severity: { Critical: 5, High: 4, Medium: 3, Low: 2, Info: 1 },
  status: { Open: 6, Reopened: 5, Retest: 4, 'In Progress': 3, Accepted: 2, Closed: 1 },
};

const sortValueOf = (row, col) => {
  if (typeof col.sortValue === 'function') return col.sortValue(row);
  const raw = row[col.key];
  const rank = RANKS[col.key];
  if (rank && rank[raw] != null) return rank[raw];
  if (raw == null || raw === '') return null;      // always sorts last
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isNaN(n) && String(raw).trim() !== '') return n;
  return String(raw).toLowerCase();
};

export const Table = ({ columns, data, onRowClick, onRowDoubleClick, rowStyle, expandRow, expandedIds, onToggleExpand, sortable = false }) => {
  // null = the order the caller supplied, which is often deliberate. The first
  // click sorts descending, because on a findings table the interesting end is
  // the top one.
  const [sort, setSort] = React.useState(null);

  const toggleSort = (col) => {
    if (!sortable || col.sortable === false) return;
    setSort(prev => (!prev || prev.key !== col.key)
      ? { key: col.key, dir: 'desc' }
      : (prev.dir === 'desc' ? { key: col.key, dir: 'asc' } : null));
  };

  const rows = React.useMemo(() => {
    if (!sort) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return data;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = sortValueOf(a, col), bv = sortValueOf(b, col);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;            // blanks last in both directions
      if (bv === null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor;
    });
  }, [data, sort, columns]);

  const [expanded, setExpanded] = React.useState({});
  const isControlled = expandedIds !== undefined;
  const isRowExpanded = (id) => (isControlled ? !!expandedIds[id] : !!expanded[id]);
  const toggleExpand = (id, e) => {
    e.stopPropagation();
    if (isControlled) onToggleExpand?.(id);
    else setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };
  return (
  <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--border)' }}>
          {expandRow && <th style={{ padding: '11px 8px', width: 32, background: 'var(--bg-input)' }} />}
          {columns.map(c => {
            const canSort = sortable && c.sortable !== false;
            const active = sort && sort.key === c.key;
            return (
            <th key={c.key}
              onClick={canSort ? () => toggleSort(c) : undefined}
              title={canSort ? `Sort by ${c.label}` : undefined}
              style={{
                padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.5px',
                color: active ? 'var(--text)' : 'var(--text-secondary)',
                whiteSpace: 'nowrap', background: 'var(--bg-input)',
                cursor: canSort ? 'pointer' : 'default',
                userSelect: canSort ? 'none' : 'auto',
              }}>
              {c.label}
              {canSort && (
                <span style={{
                  marginLeft: 6, fontSize: 9,
                  opacity: active ? 1 : 0.35,
                }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
              )}
            </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length + (expandRow ? 1 : 0)} style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No data found</td></tr>
        ) : rows.map((row, i) => {
          const rowId = row.id || i;
          const customStyle = rowStyle ? rowStyle(row) : {};
          const defaultBg = customStyle.background || 'transparent';
          const isExpanded = isRowExpanded(rowId);
          const expandContent = expandRow ? expandRow(row) : null;
          return (
          <React.Fragment key={rowId}>
          <tr onClick={() => onRowClick?.(row)} onDoubleClick={() => onRowDoubleClick?.(row)}
            style={{ borderTop: '1px solid var(--border)', cursor: onRowClick ? 'pointer' : 'default', transition: 'background 0.15s', ...customStyle }}
            onMouseOver={e => { e.currentTarget.style.background = customStyle.background ? customStyle.background + 'cc' : 'var(--bg-input)'; }}
            onMouseOut={e => { e.currentTarget.style.background = defaultBg; }}>
            {expandRow && (
              <td style={{ padding: '12px 8px', width: 32, textAlign: 'center' }}>
                {expandContent && (
                  <span onClick={e => toggleExpand(rowId, e)}
                    style={{ cursor: 'pointer', fontSize: 12, userSelect: 'none', color: 'var(--text-secondary)' }}>
                    {isExpanded ? '▾' : '▸'}
                  </span>
                )}
              </td>
            )}
            {columns.map(c => (
              <td key={c.key} style={{ padding: '12px 16px', fontSize: 14 }}>
                {c.render ? c.render(row[c.key], row) : row[c.key]}
              </td>
            ))}
          </tr>
          {isExpanded && expandContent && (
            <tr style={{ background: 'var(--bg-input)' }}>
              <td colSpan={columns.length + 1} style={{ padding: '12px 16px 16px 48px' }}>
                {expandContent}
              </td>
            </tr>
          )}
          </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};

export const StatCard = ({ label, value, icon, color = 'var(--primary)', iconBg }) => (
  <Card style={{ padding: '18px 20px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: iconBg || '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
      }}>{icon}</div>
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-1px', color: color }}>{value}</div>
  </Card>
);

/**
 * A report count that doubles as the way in: clicking opens the engagement
 * already on its Reports tab. Used by the dashboard and the engagements list,
 * which is why it lives here — one definition of the pill and of the link.
 *
 * `navigate` is passed in rather than taken from useNavigate so this file stays
 * router-free, as the rest of it is.
 *
 * stopPropagation is load-bearing: both call sites sit inside a row that has
 * its own click handler (the dashboard navigates, the engagements list opens a
 * drawer), and without it that handler fires next and undoes the trip.
 *
 * Zero still links — the Reports tab is where a report gets generated or
 * uploaded, so it is a useful destination rather than a dead end.
 */
export const ReportCountLink = ({ count, engagementId, navigate }) => {
  // COUNT() arrives as a string from pg, so "0" would be truthy. Coerce first.
  const n = Number(count) || 0;
  return (
    <button
      onClick={ev => { ev.stopPropagation(); navigate(`/engagements/${engagementId}?tab=reports`); }}
      onDoubleClick={ev => ev.stopPropagation()}
      title={n === 0 ? 'No reports yet — open the Reports tab' : `View ${n} report${n === 1 ? '' : 's'}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 24, height: 22, padding: '0 8px', borderRadius: 6,
        border: '1px solid transparent', fontFamily: 'inherit', cursor: 'pointer',
        fontSize: 12, fontWeight: 700,
        background: n > 0 ? 'rgba(79,143,255,0.12)' : 'rgba(100,116,139,0.08)',
        color: n > 0 ? 'var(--primary)' : 'var(--text-muted)',
      }}
      onMouseOver={ev => { ev.currentTarget.style.borderColor = 'var(--primary)'; }}
      onMouseOut={ev => { ev.currentTarget.style.borderColor = 'transparent'; }}
    >{n}</button>
  );
};
